import PrimaryButton from "canbook-style/ui/button/primary";
import { ButtonType } from "canbook-style/ui/button/models";
import ErrorPrompt from "canbook-style/ui/prompt/error";
import CountryCodeDropdown from "canbook-style/ui/country-code-dropdown";
import { useState, useEffect } from "react";
import ProfileImageEdit from "@/components/user/profile-image";
import { getPhoneLengthByDialCode } from "canbook-data/util/country-codes";
import { useProfileState } from "../profile-state";
import { ProfileComponentCallback } from "../hooks";

export function ProfileComponent({
  userId,
  phone,
  email,
  callback,
}: {
  userId: string;
  phone: string;
  email: string;
  callback: ProfileComponentCallback;
}) {
  const {
    user,
    apiError,
    name,
    email: emailState,
    phone: phoneState,
    countryCode: profileCountryCode,
    profileUrl,
    errors,
    isInProgress,
    isEmailLogin,
    setErrors,
    setApiError,
    setName,
    setEmail,
    setPhone,
    setCountryCode: setProfileCountryCode,
    setIsImageModalOpen,
    isImageModalOpen,
    handleProfileSubmit,
    handleFileUploading,
    removeProfileImage,
    maxPhoneLength,
    checkValidation,
  } = useProfileState(userId, phone, email, callback);

  const handleCountryCodeChange = (newCode: string) => {
    setProfileCountryCode(newCode);
    if (phoneState) {
      const newMaxLength = getPhoneLengthByDialCode(newCode);
      const trimmed = phoneState.slice(0, newMaxLength);
      setPhone(trimmed);
      checkValidation(trimmed, newCode);
    }
  };
  const [buttonOffset, setButtonOffset] = useState(0);
  const [currentScroll, setCurrentScroll] = useState<number | undefined>(0);

  useEffect(() => {
    const handleScroll = () => {
      setCurrentScroll(window.visualViewport?.offsetTop);
    };

    const handleResize = () => {
      if (window.visualViewport) {
        const viewportHeight = window.visualViewport.height;
        setButtonOffset(window.innerHeight - viewportHeight);
      }
    };

    window.visualViewport?.addEventListener("resize", handleResize);
    window.visualViewport?.addEventListener("scroll", handleScroll);

    return () => {
      window.visualViewport?.removeEventListener("resize", handleResize);
      window.visualViewport?.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
      <div className="px-4 w-full lg:w-1/2 xl:w-1/3 flex lg:items-center lg:justify-center mt-20 lg:mt-0">
        <div className="w-full sm:w-4/6 lg:w-3/4 bg-surface mx-auto flex flex-col gap-12">
          <h1 className="d-header-1 text-font-primary text-center lg:text-left">
            Setup your profile
          </h1>
          <div>
            <ProfileImageEdit
              user={{ name: user?.user_name, profile_image_url: profileUrl }}
              isImageModalOpen={isImageModalOpen}
              setIsImageModalOpen={setIsImageModalOpen}
              handleFileUploading={handleFileUploading}
              removeProfileImage={removeProfileImage}
              isInLoginFlow={true}
            />
            <form className="flex flex-col w-full gap-6">
              <div className="flex flex-col w-full">
                <label className="body-2 text-font-disabled lg:text-font-secondary pl-3 pb-2">
                  Name
                </label>
                <div className="flex relative w-full subheader-1">
                  <input
                    aria-label="Name"
                    type="text"
                    name="name"
                    className="border border-outline bg-container-low py-3 px-6 rounded-xl w-full outline-none focus:border-primary caret-primary text-font-primary"
                    onChange={(e) => {
                      setName(e.target.value);
                      setErrors(errors.filter((error) => error.for !== "name"));
                    }}
                    value={name}
                    autoComplete="off"
                  />
                </div>
                <div className="mt-1 error-message">
                  {errors.find((error) => error.for == "name")?.message}
                </div>
              </div>
              <div className="flex flex-col w-full">
                <label className="body-2 text-font-disabled lg:text-font-secondary pl-3 pb-2">
                  Email
                </label>
                <div className="flex relative w-full subheader-1">
                  <input
                    aria-label="Email"
                    type="text"
                    name="email"
                    className="border border-outline bg-container-low py-3 px-6 rounded-xl w-full outline-none focus:border-primary caret-primary text-font-primary disabled:text-font-disabled disabled:bg-container-low"
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrors(
                        errors.filter((error) => error.for !== "email"),
                      );
                    }}
                    value={emailState}
                    disabled={isEmailLogin}
                    autoComplete="off"
                  />
                </div>
                <div className="mt-1 error-message">
                  {errors.find((error) => error.for == "email")?.message}
                </div>
              </div>
              <div className="flex flex-col w-full mb-5 lg:mb-0">
                <label className="body-2 text-font-disabled lg:text-font-secondary pl-3 pb-2">
                  Phone <span className="text-primary">*</span>
                </label>
                <div className="flex w-full subheader-1 gap-3">
                  {isEmailLogin ? (
                    <>
                      <CountryCodeDropdown
                        value={profileCountryCode}
                        onChange={handleCountryCodeChange}
                      />
                      <input
                        aria-label="Phone number"
                        type="tel"
                        inputMode="numeric"
                        name="phone"
                        className="border border-outline bg-container-low py-3 px-4 rounded-xl w-full text-font-primary outline-none focus:border-primary caret-primary"
                        onChange={(e) => {
                          const inputVal = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, maxPhoneLength);
                          setPhone(inputVal);
                          checkValidation(inputVal);
                        }}
                        value={phoneState}
                        maxLength={maxPhoneLength}
                        autoComplete="tel"
                      />
                    </>
                  ) : (
                    <input
                      aria-label="Phone number"
                      type="text"
                      name="phone"
                      className="border border-outline bg-container-low py-3 px-6 rounded-xl w-full text-font-disabled subheader-1"
                      value={`${profileCountryCode} ${phoneState}`}
                      disabled={true}
                    />
                  )}
                </div>
                <div className="mt-1 error-message">
                  {
                    errors.find(
                      (error) =>
                        error.for == "phone" || error.for == "phone_length",
                    )?.message
                  }
                </div>
              </div>
              <div className="hidden lg:block mt-4">
                <PrimaryButton
                  type={ButtonType.button}
                  text="Finish"
                  isExpanded={true}
                  isDisabled={errors.length > 0}
                  isInProgress={isInProgress}
                  onClick={handleProfileSubmit}
                />
              </div>
            </form>
          </div>
          {apiError && (
            <ErrorPrompt error={apiError} onClose={() => setApiError(null)} />
          )}
        </div>
      </div>
      <div
        className="lg:hidden fixed left-0 bottom-0 w-full px-4 bg-surface py-2"
        style={{
          transform: `translateY(-${buttonOffset - (currentScroll ?? 0)}px)`,
        }}
      >
        <PrimaryButton
          type={ButtonType.button}
          text="Finish"
          isExpanded={true}
          isDisabled={errors.length > 0}
          isInProgress={isInProgress}
          onClick={handleProfileSubmit}
        />
      </div>
    </>
  );
}

export default ProfileComponent;
